/**
 * CicloDia — amanhecer -> anoitecer.
 *
 * D12: a sessao dura de 8 a 12 minutos e termina com anoitecer NATURAL.
 * Sem tela de bloqueio, sem cronometro na tela, sem aviso de "acabou o tempo"
 * (D7). A luz simplesmente vai baixando e a fazenda vai dormir.
 *
 * Secao 12: no maximo 2 luzes dinamicas + ambiente. Aqui: sol + luz de
 * preenchimento + ambiente. Exatamente no limite.
 */

import * as THREE from 'three';

const DURACAO_SESSAO_MIN = 8;
const DURACAO_SESSAO_MAX = 12;

// Momentos do dia: [posicao do sol, cor do sol, cor do ceu, intensidade]
const MOMENTOS = [
  { t: 0.0, cor: 0xffe6c0, ceu: 0xa8d8ea, intensidade: 1.6 },  // amanhecer
  { t: 0.25, cor: 0xfff4d6, ceu: 0x8ed0e8, intensidade: 2.4 }, // manha
  { t: 0.55, cor: 0xffffff, ceu: 0x7ec8e3, intensidade: 2.6 }, // meio-dia
  { t: 0.8, cor: 0xffc478, ceu: 0xf0a868, intensidade: 1.8 },  // fim de tarde
  { t: 1.0, cor: 0x9aa8d8, ceu: 0x2c3e6b, intensidade: 0.5 },  // anoitecer
];

export class CicloDia {
  constructor(cena) {
    this.cena = cena;

    this.ambiente = new THREE.AmbientLight(0xffffff, 1.1);
    cena.add(this.ambiente);

    this.sol = new THREE.DirectionalLight(0xffffff, 2.4);
    this.sol.position.set(6, 12, 8);
    cena.add(this.sol);

    // Luz de preenchimento fraca, para o lado escuro nao virar breu.
    this.preenchimento = new THREE.DirectionalLight(0xbcd4ff, 0.5);
    this.preenchimento.position.set(-6, 4, -6);
    cena.add(this.preenchimento);

    this.duracaoSegundos =
      (DURACAO_SESSAO_MIN + Math.random() * (DURACAO_SESSAO_MAX - DURACAO_SESSAO_MIN)) * 60;

    this.decorrido = 0;
    this.pausado = false;
    this.aoAnoitecer = null;
    this.jaAnoiteceu = false;

    this.aplicar(0);
  }

  /** Progresso do dia, de 0 (amanhecer) a 1 (anoitecer). */
  get progresso() {
    return Math.min(1, this.decorrido / this.duracaoSegundos);
  }

  atualizar(dt) {
    if (this.pausado) return;

    this.decorrido += dt;
    this.aplicar(this.progresso);

    if (this.progresso >= 1 && !this.jaAnoiteceu) {
      this.jaAnoiteceu = true;
      console.info('FAZENDA: anoiteceu. Fim natural da sessao (D12).');
      this.aoAnoitecer?.();
    }
  }

  /**
   * Empurra o dia direto para o anoitecer.
   * Usado quando as tarefas da sessao acabam antes do tempo: a fazenda dorme
   * porque o trabalho terminou, nao porque um cronometro estourou.
   */
  apressarAnoitecer() {
    if (this.progresso < 0.8) this.decorrido = this.duracaoSegundos * 0.8;
  }

  aplicar(t) {
    const { cor, ceu, intensidade } = interpolarMomento(t);

    this.sol.color.setHex(cor);
    this.sol.intensity = intensidade;
    this.ambiente.intensity = 0.9 + intensidade * 0.35;

    // O sol atravessa o ceu de leste a oeste.
    const angulo = Math.PI * (0.12 + t * 0.76);
    this.sol.position.set(Math.cos(angulo) * 12, Math.max(1.5, Math.sin(angulo) * 14), 8);

    if (this.cena.background instanceof THREE.Color) {
      this.cena.background.setHex(ceu);
    } else {
      this.cena.background = new THREE.Color(ceu);
    }
  }
}

function interpolarMomento(t) {
  let anterior = MOMENTOS[0];
  let proximo = MOMENTOS.at(-1);

  for (let i = 0; i < MOMENTOS.length - 1; i++) {
    if (t >= MOMENTOS[i].t && t <= MOMENTOS[i + 1].t) {
      anterior = MOMENTOS[i];
      proximo = MOMENTOS[i + 1];
      break;
    }
  }

  const intervalo = proximo.t - anterior.t || 1;
  const fracao = (t - anterior.t) / intervalo;

  return {
    cor: misturarCor(anterior.cor, proximo.cor, fracao),
    ceu: misturarCor(anterior.ceu, proximo.ceu, fracao),
    intensidade: anterior.intensidade + (proximo.intensidade - anterior.intensidade) * fracao,
  };
}

function misturarCor(a, b, t) {
  const corA = new THREE.Color(a);
  const corB = new THREE.Color(b);
  return corA.lerp(corB, t).getHex();
}
